import { useState, useEffect, useRef } from 'react';
import { Database, ChevronsUpDown } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../../../../components/ui/select';
import { useDatabaseList } from '../../../database-objects/hooks/useDatabaseList';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { DatabaseListingFallbackBanner } from '../../../connection-diagnostics/components/DatabaseListingFallbackBanner';
import { UnverifiedDatabaseIndicator } from '../../../connection-diagnostics/components/UnverifiedDatabaseIndicator';
import { useFallbackPresentation } from '../../../connection-diagnostics/hooks/useFallbackPresentation';
import { getDatabaseDisplayName } from '../utils/databaseDisplayName';
import { SELECTOR_TRIGGER_CLASS, SelectorTriggerTooltip } from './SelectorTriggerTooltip';

interface DatabaseSelectorProps {
  connectionId?: string;
  database?: string;
  onDatabaseChange: (database: string) => void;
  className?: string;
  /**
   * Open-request signal: each increment pops the dropdown open (used by the
   * query-error "Switch database…" action). Undefined/0 = never signalled.
   */
  openNonce?: number;
  /** Tooltip line naming what the trigger does. No tooltip when absent. */
  actionHint?: string;
  /** User open/close of the list. Not called for an `openNonce` open. */
  onOpenChange?: (open: boolean) => void;
}

export function DatabaseSelector({
  connectionId,
  database,
  onDatabaseChange,
  className = '',
  openNonce,
  actionHint,
  onOpenChange,
}: DatabaseSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Get the connection to determine engine
  // Check both saved connections and active connections (for browser mode SQLite)
  const connections = useConnectionStore((state) => state.connections);
  const activeConnections = useConnectionStore((state) => state.activeConnections);
  const engine = connectionId
    ? connections.find((c) => c.id === connectionId)?.engine ||
      activeConnections.find((c) => c.id === connectionId)?.connection.engine ||
      'mssql'
    : 'mssql';

  // Shared per-connection listing (the Object Explorer's own cache entry),
  // stale-while-revalidate: an already-listed connection paints its databases
  // the instant the dropdown opens, and a real listing still fires on every
  // open. `databases` is `[]` unless `state === 'ok'` (Slice 5b), so the
  // failure branch below is unchanged.
  const {
    databases,
    state: databasesState,
    isInitialLoading,
  } = useDatabaseList({ connectionId, engine, enabled: isOpen });

  const fallback = useFallbackPresentation({ connectionId, state: databasesState });

  // External open request ("Switch database…" under an object-not-found query
  // error) — opening via state reuses the on-open revalidation above. The
  // ref seeds from the CURRENT nonce so a remount with a stale nonce > 0
  // never pops the dropdown uninvited; only a fresh increment opens it.
  const lastOpenNonceRef = useRef(openNonce ?? 0);
  useEffect(() => {
    if (openNonce && openNonce !== lastOpenNonceRef.current) {
      lastOpenNonceRef.current = openNonce;
      setIsOpen(true);
    }
  }, [openNonce]);

  const hasDatabase = !!database;
  const { displayName, fullPath } = getDatabaseDisplayName(database, engine);
  const displayValue = displayName || 'No database';

  if (!connectionId) {
    return (
      <div
        data-testid="database-selector-not-connected"
        className={`flex items-center gap-1 flex-shrink-0 text-muted-foreground ${className}`}
      >
        <Database className="w-3 h-3" />
        <span>Not connected</span>
      </div>
    );
  }

  const handleValueChange = (value: string) => {
    // Convert __none__ back to empty string to clear the database
    onDatabaseChange(value === '__none__' ? '' : value);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  // A SQLite file shows its base name; the full path goes on hover.
  const pathHint = fullPath && fullPath !== displayValue ? fullPath : undefined;
  const tooltipContent = actionHint ? (
    <span className="flex flex-col gap-0.5">
      {pathHint && <span className="font-medium break-all">{pathHint}</span>}
      <span>{actionHint}</span>
    </span>
  ) : undefined;

  return (
    <Select
      value={database || '__none__'}
      onValueChange={handleValueChange}
      onOpenChange={handleOpenChange}
      open={isOpen}
    >
      <SelectorTriggerTooltip content={tooltipContent} selectOpen={isOpen}>
        <SelectTrigger
          data-testid="database-selector-trigger"
          title={actionHint ? undefined : pathHint}
          // The amber no-database state comes after `className` so a caller's
          // text colour cannot wash the warning out.
          className={`
            ${SELECTOR_TRIGGER_CLASS} text-muted-foreground ${className}
            ${hasDatabase ? '' : 'text-warning font-semibold bg-amber-500/10 hover:bg-amber-500/20 hover:text-warning aria-expanded:bg-amber-500/20 aria-expanded:text-warning'}
          `}
        >
          <Database className={`w-3 h-3 flex-shrink-0 ${hasDatabase ? 'text-muted-foreground' : ''}`} />
          <span data-testid="database-selector-value" className="truncate">{displayValue}</span>
          <ChevronsUpDown className={`w-3 h-3 flex-shrink-0 ${hasDatabase ? 'text-muted-foreground' : ''}`} />
        </SelectTrigger>
      </SelectorTriggerTooltip>
      <SelectContent
        data-testid="database-selector-content"
        className="max-h-60 overflow-auto min-w-[120px]"
        position="popper"
        side="top"
        align="start"
      >
        {isInitialLoading ? (
          <div data-testid="database-selector-loading" className="px-2 py-1.5 text-xs text-muted-foreground">
            Loading databases...
          </div>
        ) : databasesState === 'error' || databasesState === 'empty' ? (
          <div
            data-testid="database-selector-listing-failed"
            className="flex flex-col gap-2 p-2"
          >
            {connectionId && (
              <DatabaseListingFallbackBanner
                connectionId={connectionId}
                variant="subline"
                trigger={databasesState === 'empty' ? 'list_empty' : 'list_failure'}
                beforeOpenDetails={() => setIsOpen(false)}
                data-testid="database-selector-fallback-banner"
              />
            )}
            {fallback.entries.length > 0 && (
              <div className="flex flex-col" data-testid="database-selector-fallback-entries">
                {fallback.entries.map((db) => (
                  <SelectItem key={db} value={db} className="text-xs">
                    <span className="flex items-center gap-1.5">
                      <span>{db}</span>
                      <UnverifiedDatabaseIndicator
                        kind={fallback.isConfig(db) ? 'config' : 'known'}
                        density="compact"
                        data-testid={`database-selector-fallback-indicator-${db}`}
                      />
                    </span>
                  </SelectItem>
                ))}
              </div>
            )}
          </div>
        ) : databases.length === 0 ? (
          <div data-testid="database-selector-empty" className="px-2 py-1.5 text-xs text-muted-foreground">
            No databases found
          </div>
        ) : (
          <>
            <SelectItem value="__none__" className="text-xs text-muted-foreground italic">
              No database
            </SelectItem>
            {databases.map((db) => (
              <SelectItem key={db} value={db} className="text-xs" data-testid={`database-option-${db}`}>
                {db}
              </SelectItem>
            ))}
          </>
        )}
      </SelectContent>
    </Select>
  );
}
